import { useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import Grid from '@mui/material/Grid';

import FormikTextField from '@/components/Formik/FormikTextField';
import { PrimaryButton } from '@/components/common';
import { recaptchaSiteKey } from '@/constants/configs';
import { onPushEvent } from '@/services/lib/gtm';
import { useTranslation } from '@/hooks/translations';
import type { IBaseLoginProps } from '@/views/Login/login';
import AuthService from '@/services/auth';
import Box from '@mui/material/Box';

interface IDeviceLoginProps extends IBaseLoginProps {}

const DeviceLogin = ({
    setLoading,
    setLoginResponse,
    setPage,
    accessSelectRestaurant,
    setError,
}: IDeviceLoginProps) => {
    const { t } = useTranslation('common');
    const authService = AuthService.getInstance();
    const { enqueueSnackbar } = useSnackbar();

    const recaptchaRef = useRef<ReCAPTCHA | null>(null);

    const formik = useFormik({
        initialValues: {
            otp: '',
        },
        validationSchema: Yup.object({
            otp: Yup.string().matches(/^\d+$/, 'Only digits are allowed').required(t('OTP is required')),
        }),
        onSubmit: (values, { setSubmitting }) => {
            setError('');
            setLoading(true);
            recaptchaRef.current
                ?.executeAsync()
                .then((value) => {
                    return authService
                        .deviceLogin({ ...values, recaptcha: value || '' })
                        .then((res) => {
                            onPushEvent('user_successfully_logged_in');
                            setLoginResponse(res);
                            accessSelectRestaurant(res, {
                                withOtp: true,
                            });
                        })
                        .catch(() => {
                            onPushEvent('user_login_failed');
                            enqueueSnackbar(t('Incorrect credentials'), { variant: 'error' });
                        });
                })
                .finally(() => {
                    setSubmitting(false);
                    setLoading(false);
                });
        },
    });

    const { handleReset, handleSubmit, isSubmitting } = formik;

    return (
        <FormikProvider value={formik}>
            <form onSubmit={handleSubmit} onReset={handleReset}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormikTextField
                            name="otp"
                            label=""
                            placeholder={t('OTP')}
                            type="text"
                            required
                            fullWidth
                            preChangeFunction={(str: string) => {
                                return str.split(' ').join('');
                            }}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                style: { textAlign: 'center', fontSize: '1rem', letterSpacing: '0.3rem' },
                            }}
                        />
                    </Grid>
                </Grid>
                <PrimaryButton
                    id="login-btn"
                    disabled={isSubmitting}
                    type="submit"
                    text={isSubmitting ? t('Logging in') : t('Login')}
                    fullWidth
                    styles={{
                        marginTop: '1rem',
                        fontWeight: 700,
                        fontSize: '1rem',
                        borderRadius: '0.5rem',
                    }}
                />
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.5rem 0',
                    }}
                >
                    {t('or')}
                </Box>
                <PrimaryButton
                    id="device-login-btn"
                    disabled={isSubmitting}
                    type="button"
                    text={t('Login with Password')}
                    fullWidth
                    onClick={() => {
                        setPage('login');
                    }}
                    sx={{
                        fontSize: '0.8rem',
                        borderRadius: '0.5rem',
                        backgroundColor: 'transparent',
                        color: 'var(--iris)',
                        border: '1px solid var(--iris)',
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    }}
                />
            </form>
            {recaptchaSiteKey && <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaSiteKey} size="invisible" />}
        </FormikProvider>
    );
};

export default DeviceLogin;
